import { Dispatch, SetStateAction, useState } from "react";
import { HttpError } from "../../../functions/httpClient";
import { updateContactGraph, createClientGraph } from "../../../functions/knowledgeGraphApi";
import { createContact, updateContact, mergeContact } from "../../../services/contacts.service";
import { createClient } from "../../../services/clients.service";
import { linkContact, updateRelationship } from "../../../services/relationships.service";
import { ClientLinkState } from "./useClientLink";

const CONTACT_PHONE_REGEX = /^(\+61[0-9]{9}|0[0-9]{9}|\+?[0-9]{10,15})$/;
const isValidPhone = (value: string): boolean =>
  CONTACT_PHONE_REGEX.test(value.replace(/[\s\-\(\)]/g, ""));

type RelationshipForm = { relationshipType: string; isPrimary: boolean };

interface UseContactSaveArgs {
  contactData?: any;
  relationshipData?: {
    relationshipId?: string;
    relationshipType?: string;
    isPrimary?: boolean;
    clientId?: string;
  };
  allLinkedContacts: any[];
  isEditMode: boolean;
  hasRelationship: boolean;
  formData: any;
  relationshipForm: RelationshipForm;
  setRelationshipForm: Dispatch<SetStateAction<RelationshipForm>>;
  clientLink: ClientLinkState;
  login: any;
  setAlert: (message: string, severity: string) => void;
  onSave: (newContact?: any) => void;
  onClose: () => void;
}

/**
 * Owns the entire contact save flow: validation, create/update, the
 * duplicate-contact conflict dialog, the client-link side effect, and the
 * primary-contact-swap confirmation dialog.
 */
export function useContactSave({
  contactData,
  relationshipData,
  allLinkedContacts,
  isEditMode,
  hasRelationship,
  formData,
  relationshipForm,
  setRelationshipForm,
  clientLink,
  login,
  setAlert,
  onSave,
  onClose,
}: UseContactSaveArgs) {
  const [isSaving, setIsSaving] = useState(false);
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [duplicateContact, setDuplicateContact] = useState<any>(null);
  const [pendingPayload, setPendingPayload] = useState<any>(null);

  const [primaryConfirmOpen, setPrimaryConfirmOpen] = useState(false);
  const [pendingPrimaryPayload, setPendingPrimaryPayload] = useState<any>(null);
  const [existingPrimaryContact, setExistingPrimaryContact] = useState<any>(null);

  // Tracks whether the contact record was already saved during this flow
  // so we don't re-save it when the user confirms the primary contact swap.
  const [contactAlreadySaved, setContactAlreadySaved] = useState(false);
  // Holds the newly created contact so we can pass it to onSave after the
  // relationship update succeeds (even if that happens in a second attempt).
  const [pendingNewContact, setPendingNewContact] = useState<any>(null);

  const resetSaveState = () => {
    setContactAlreadySaved(false);
    setPendingNewContact(null);
  };

  const buildPayload = (fullName: string) => {
    const payload: any = {
      name: fullName,
      email: formData.email,
      phone: formData.phone,
      kyc: formData.kyc,
      admin: formData.admin,
      updatedBy: login.data.id,
    };
    if (!isEditMode) {
      payload.orgId = login.data.orgId;
      payload.userId = login.data.id;
      payload.createdBy = login.data.id;
    }
    return payload;
  };

  /**
   * Extracts the display name from an allLinkedContacts entry. These entries
   * can be raw contact objects ({ _id, name, isPrimary }) OR
   * relationship-populated objects ({ contactId: { _id, name }, isPrimary }).
   */
  const getContactName = (entry: any): string =>
    entry?.name || entry?.contactId?.name || "Existing Contact";

  /** Finds another linked contact that is already primary, handling both flat and populated shapes. */
  const findOtherPrimary = (): any | undefined => {
    const currentId = contactData?._id?.toString();
    return allLinkedContacts.find((c) => {
      if (!c.isPrimary) return false;
      const flatId = c._id?.toString();
      const nestedId = c.contactId?._id?.toString() || c.contactId?.toString();
      const idToCompare = nestedId || flatId;
      return idToCompare !== currentId;
    });
  };

  /**
   * Separated from executeSave so it can be retried on its own (forcePrimary=true)
   * after a primary-conflict confirmation, without re-saving the contact.
   */
  const executeRelationshipUpdate = async (
    contactPayload: any,
    forcePrimary: boolean,
    savedContact: any | null,
  ) => {
    const relationshipPayload = {
      relationshipId: relationshipData!.relationshipId,
      relationshipType: relationshipForm.relationshipType,
      isPrimary: relationshipForm.isPrimary,
      forcePrimary,
    };

    let relationshipResponse: any;
    try {
      relationshipResponse = await updateRelationship(relationshipPayload);
    } catch (relError: any) {
      if (!forcePrimary) {
        const data = relError?.data || {};
        if (
          data.existingPrimary ||
          /already.*primary|primary.*already/i.test(relError?.message || "")
        ) {
          setIsSaving(false);
          setContactAlreadySaved(true);
          setExistingPrimaryContact(
            data.existingPrimary || { name: "existing contact" },
          );
          setPendingPrimaryPayload(contactPayload);
          setPrimaryConfirmOpen(true);
          return false;
        }
      }
      throw relError;
    }

    // Some setups return the body (not throw) for non-2xx
    if (
      !forcePrimary &&
      relationshipResponse?.error &&
      (relationshipResponse.existingPrimary ||
        /already.*primary|primary.*already/i.test(
          relationshipResponse.error || "",
        ))
    ) {
      setIsSaving(false);
      setContactAlreadySaved(true);
      setExistingPrimaryContact(
        relationshipResponse.existingPrimary || { name: "existing contact" },
      );
      setPendingPrimaryPayload(contactPayload);
      setPrimaryConfirmOpen(true);
      return false;
    }

    if (relationshipResponse?.error) {
      throw new Error(relationshipResponse.error);
    }

    if (
      relationshipData?.clientId &&
      contactData?._id &&
      login?.data?.id &&
      login?.data?.orgId
    ) {
      updateContactGraph({
        clientId: relationshipData.clientId,
        _id: contactData._id,
        relationshipType: relationshipForm.relationshipType,
        isPrimary: relationshipForm.isPrimary,
        orgId: login.data.orgId,
        userId: login.data.id,
      }).catch(() => {});
    }

    setAlert("Contact updated successfully!", "success");
    onSave(savedContact ?? null);
    try {
      window.dispatchEvent(new Event("contactsUpdated"));
    } catch (e) {
      console.log("contactsUpdated event dispatch failed:", e);
    }
    onClose();
    return true;
  };

  const handlePrimaryConfirm = async () => {
    setPrimaryConfirmOpen(false);
    setExistingPrimaryContact(null);

    if (!pendingPrimaryPayload) return;

    setIsSaving(true);
    try {
      const done = await executeRelationshipUpdate(
        pendingPrimaryPayload,
        true,
        pendingNewContact,
      );
      if (done) setPendingPrimaryPayload(null);
    } catch (error: any) {
      setAlert(error?.message || "Failed to update relationship.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrimaryCancel = () => {
    setPrimaryConfirmOpen(false);
    setExistingPrimaryContact(null);
    setPendingPrimaryPayload(null);
    setRelationshipForm((prev) => ({ ...prev, isPrimary: false }));
  };

  const executeSave = async (
    payload: any,
    force: boolean = false,
    forcePrimary: boolean = false,
  ) => {
    setIsSaving(true);
    try {
      // Skip re-saving if the contact was already saved earlier in this flow
      // (e.g. contact saved OK but relationship returned 409, user confirmed).
      let savedContact: any = pendingNewContact ?? null;

      if (!contactAlreadySaved) {
        const response: any = isEditMode
          ? await updateContact(contactData._id, payload)
          : await createContact(payload, force);

        // Duplicate contact detected (returned as body, not thrown)
        if (!isEditMode && !force && response.error && response.duplicate) {
          setIsSaving(false);
          setDuplicateContact(response.duplicate);
          setPendingPayload(payload);
          setConflictModalOpen(true);
          return;
        }

        if (response.error && !response.success)
          throw new Error(response.error || response.message);

        if (!isEditMode) {
          savedContact = response.contact;
          setPendingNewContact(response.contact);
        }
      }

      // Link to client (new contact creation only)
      if (!isEditMode && clientLink.mode !== "none") {
        try {
          let linkedClientId: string | null = null;
          if (clientLink.mode === "new") {
            const clientResp: any = await createClient({
              name: clientLink.newClientName.trim(),
              entityType: clientLink.newClientEntityType,
              orgId: login.data.orgId,
              userId: login.data.id,
              createdBy: login.data.id,
              profile: { clientStatus: "Active" },
            });
            if (clientResp.error) throw new Error(clientResp.error || "Failed to create client.");
            linkedClientId = clientResp._id || clientResp.id || null;
            if (linkedClientId) {
              createClientGraph(
                { _id: linkedClientId, name: clientResp.name, entityType: clientResp.entityType,
                  orgId: clientResp.orgId || login.data.orgId, contactId: clientResp.contactId,
                  address: clientResp.address || {}, profile: clientResp.profile || {} },
                { userId: login.data.id, orgId: login.data.orgId }
              ).catch((err) => console.error("createClientGraph failed:", err));
            }
          } else {
            linkedClientId =
              clientLink.selectedClient?._id || clientLink.selectedClient?.id || null;
          }
          const newContactId = savedContact?._id || savedContact?.id;
          if (linkedClientId && newContactId) {
            await linkContact({
              clientId: linkedClientId,
              contactId: newContactId,
              relationshipType: clientLink.relationshipType,
              isPrimary: false,
              orgId: login.data.orgId,
              userId: login.data.id,
            });
          }
        } catch (linkError: any) {
          setAlert(
            `Contact created, but failed to link to client: ${linkError?.message || "Unknown error"}`,
            "warning",
          );
          onSave(savedContact);
          try { window.dispatchEvent(new Event("contactsUpdated")); } catch {}
          onClose();
          return;
        }
      }

      // Update relationship (if this modal is for a linked contact)
      if (hasRelationship && relationshipData?.relationshipId) {
        // executeRelationshipUpdate returns false when it has opened the
        // primary-conflict confirmation dialog — in that case we stop here.
        const done = await executeRelationshipUpdate(
          payload,
          forcePrimary,
          savedContact,
        );
        if (!done) return;
        // executeRelationshipUpdate already called setAlert / onSave / onClose
        return;
      }

      // No relationship to update — we're done
      setAlert(
        isEditMode
          ? "Contact updated successfully!"
          : "Contact created successfully!",
        "success",
      );
      onSave(isEditMode ? null : savedContact);
      try {
        window.dispatchEvent(new Event("contactsUpdated"));
      } catch (e) {
        console.log("contactsUpdated event dispatch failed:", e);
      }
      onClose();
    } catch (caughtError: unknown) {
      const error: any = caughtError;

      // Duplicate contact thrown as HttpError 409
      if (!isEditMode && !force) {
        if (
          (error instanceof HttpError &&
            error.status === 409 &&
            (error.data as any)?.duplicate) ||
          (error?.status === 409 && (error.data as any)?.duplicate)
        ) {
          setIsSaving(false);
          setDuplicateContact((error.data as any).duplicate);
          setPendingPayload(payload);
          setConflictModalOpen(true);
          return;
        }
      }

      setAlert(
        error?.message ||
          `Failed to ${isEditMode ? "update" : "create"} contact.`,
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!formData.firstName.trim()) {
      return setAlert("First Name is required.", "warning");
    }
    if (!/[a-zA-Z]/.test(formData.firstName.trim())) {
      return setAlert("First name cannot be purely numerical.", "warning");
    }
    if (formData.surname.trim() && !/[a-zA-Z]/.test(formData.surname.trim())) {
      return setAlert("Last Name cannot be purely numerical.", "warning");
    }
    if (
      formData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      return setAlert("Please enter a valid email address.", "warning");
    }
    if (formData.phone.trim() && !isValidPhone(formData.phone)) {
      return setAlert(
        "Enter a valid phone number (e.g. +61412345678 or 0412345678).",
        "warning",
      );
    }
    if (hasRelationship && !relationshipForm.relationshipType) {
      return setAlert("Relationship Type is required.", "warning");
    }
    if (!isEditMode && clientLink.mode === "new" && !clientLink.newClientName.trim()) {
      return setAlert("Client name is required when creating a new client.", "warning");
    }

    const fullName =
      `${formData.firstName.trim()} ${formData.surname.trim()}`.trim();
    const payload = buildPayload(fullName);

    // Client-side primary conflict check for instant feedback — the backend
    // is also the authoritative guard (handled in executeRelationshipUpdate).
    if (hasRelationship && relationshipForm.isPrimary) {
      const otherPrimary = findOtherPrimary();
      if (otherPrimary) {
        setExistingPrimaryContact({
          ...otherPrimary,
          name: getContactName(otherPrimary),
        });
        setPendingPrimaryPayload(payload);
        setPrimaryConfirmOpen(true);
        return;
      }
    }

    await executeSave(payload, false);
  };

  const handleMerge = async () => {
    if (!duplicateContact || !pendingPayload) return;

    const contactId = duplicateContact._id || duplicateContact.id;
    if (!contactId) {
      setAlert("Error: Contact ID not found. Please try again.", "error");
      return;
    }

    setConflictModalOpen(false);
    setIsSaving(true);

    try {
      const response: any = await mergeContact(contactId, pendingPayload);

      if (response.error && !response.success)
        throw new Error(response.error || response.message);

      setAlert("Contact merged successfully!", "success");
      onSave(response.contact);
      try {
        window.dispatchEvent(new Event("contactsUpdated"));
      } catch (e) {
        console.log("contactsUpdated event dispatch failed:", e);
      }
      onClose();
    } catch (error: any) {
      setAlert(error.message || "Failed to merge contact.", "error");
    } finally {
      setIsSaving(false);
      setDuplicateContact(null);
      setPendingPayload(null);
    }
  };

  const handleCreateNew = async () => {
    if (!pendingPayload) return;
    setConflictModalOpen(false);
    await executeSave(pendingPayload, true); // force = true → bypass duplicate check
    setDuplicateContact(null);
    setPendingPayload(null);
  };

  const clearConflict = () => {
    setConflictModalOpen(false);
    setDuplicateContact(null);
    setPendingPayload(null);
  };

  return {
    isSaving,
    conflictModalOpen,
    duplicateContact,
    pendingPayload,
    clearConflict,
    primaryConfirmOpen,
    existingPrimaryContact,
    handleSave,
    handleMerge,
    handleCreateNew,
    handlePrimaryConfirm,
    handlePrimaryCancel,
    resetSaveState,
  };
}
